import type { BillingInterval,
  FixedProduct,
  NewProduct,
  Plan,
  PlanBillingData,
  Product,
  UsageProduct } from '@weft/types';
import { raise, sumBy } from '@weft/utils';
import { getBillingIntervalInMonths } from '../billing-settings-number-converter';
import { isCommercialTermsProduct,
  isCommissionProduct,
  isFixedPerUnitProduct,
  isFixedProduct, isInstallmentsProduct,
  isOneTimeProduct,
  isRefundProduct,
  isSubscriptionProduct } from '../products-filter';
import { CommercialTermsPriceEstimator,
  FixedOneTimePriceEstimator,
  FixedPerUnitPriceEstimator, InstallmentsPriceEstimator,
  RefundPriceEstimator,
  SubscriptionPriceEstimator } from './product-estimators';

export interface ProductPriceEstimator<ProductType extends Product | NewProduct = Product | NewProduct, CalculationInput = undefined> {
  estimate: (product: ProductType, calculationInput: CalculationInput) => number;
}

export function estimatePlan(plan: Plan) {
  const products = plan.products as FixedProduct[] | UsageProduct[];

  return sumBy(products, (product: typeof products[number]) => getProductEstimation(product, plan.billingData));
}

function getProductEstimation(product: FixedProduct | UsageProduct, billingData: PlanBillingData) {
  return isFixedProduct<FixedProduct>(product) ? getFixedProductEstimation(product, billingData) : getUsageProductEstimation(product, billingData.billingInterval);
}

function getFixedProductEstimation(product: FixedProduct, billingData: PlanBillingData) {
  if (isOneTimeProduct(product)) {
    return new FixedOneTimePriceEstimator().estimate(product, undefined);
  }

  if (isFixedPerUnitProduct(product)) {
    return new FixedPerUnitPriceEstimator().estimate(product, billingData.duration);
  }

  if (isSubscriptionProduct(product)) {
    return new SubscriptionPriceEstimator().estimate(product, billingData.billingInterval);
  }

  if (isCommissionProduct(product)) {
    return 0;
  }

  if (isCommercialTermsProduct(product)) {
    return new CommercialTermsPriceEstimator().estimate(product, undefined);
  }

  if (isInstallmentsProduct(product)) {
    return new InstallmentsPriceEstimator().estimate(product, undefined);
  }

  if (isRefundProduct(product)) {
    return new RefundPriceEstimator().estimate(product, undefined);
  }

  return raise(`Unknown fixed pricing model: ${product.variants[0].pricing.pricingModel}`);
}

function getUsageProductEstimation(product: UsageProduct, billingPeriod: BillingInterval) {
  const billingPeriodInMonths = getBillingIntervalInMonths(billingPeriod);

  return (product.basePrice ?? 0) * (12 / billingPeriodInMonths);
}

export * from './product-estimators';